import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import ButtonComponent from "../../components/Button.Component";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import useFetchPaginatedResearchQueryRunScoringConfigs from "../../hooks/useFetchPaginatedResearchQueryRunScoringConfigs";
import {
  deleteResearchQueryRunScoringConfig,
  reactivateResearchQueryRunScoringConfig,
} from "../../services/scoringService";
import { getActiveRunInstance } from "../../services/runInstance";
import { getResearchQueriesList } from "../../services/ResearchQueriesService";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { paginatedResponseFormat } from "../../services/constants";
import { getFormattedDateTime } from "../../services/utils";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import Loader from "../../components/Molecules/Loader/Loader";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import {
  IconEyeBase,
  IconEditBase,
  IconDeleteBlueSvg,
  IconReactivateSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
} from "../../common/utils/icons";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { formatEntityLabel } from "../../utils/scoringFormatters";
import { getConfig } from "../../config/runtimeConfig";

const FILTER_CONFIG = {
  DEFAULT_STATE: {
    run_id: "",
    generic_research_query_id: "",
    status_filter: "Active",
    severity: "",
  },
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },
  FIELD_TYPES: {
    ID_FIELDS: ["run_id", "generic_research_query_id"] as string[],
    STRING_FIELDS: ["status_filter", "severity"],
    DATE_FIELDS: [] as never[],
  },
} as const;

const ResearchQueryRunScoringList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission, isAdmin } = useAuth();
  const canEdit =
    isAdmin || hasPermission("research_query_run_scoring_config", "edit");
  const canAdd =
    isAdmin || hasPermission("research_query_run_scoring_config", "add");
  const canDelete =
    isAdmin || hasPermission("research_query_run_scoring_config", "delete");
  const { rowsPerPage } = useRowsPerPage();
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [runMap, setRunMap] = useState<Map<number, string>>(new Map());
  const [grqMap, setGrqMap] = useState<Map<number, string>>(new Map());

  const parseUrlFilter = (field: string, raw: string): any => {
    if (FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.includes(field)) {
      const n = Number(raw);
      return Number.isFinite(n) ? n : "";
    }
    return raw;
  };

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const s: any = { ...FILTER_CONFIG.DEFAULT_STATE };
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const v = searchParams.get(f);
      if (v) s[f] = parseUrlFilter(f, v);
    });
    return s;
  });

  const runInstanceId = searchFilterData.run_id
    ? parseInt(searchFilterData.run_id)
    : null;
  const genericResearchQueryId = searchFilterData.generic_research_query_id
    ? parseInt(String(searchFilterData.generic_research_query_id))
    : null;
  const isActiveFilter =
    searchFilterData.status_filter === "Active"
      ? true
      : searchFilterData.status_filter === "Inactive"
      ? false
      : undefined;
  const severityFilter: string = searchFilterData.severity || "";

  // Build id→name lookup maps on mount
  useEffect(() => {
    getActiveRunInstance()
      .then((runs) => {
        if (Array.isArray(runs)) {
          const m = new Map<number, string>();
          runs.forEach((r: any) => {
            if (r.id != null) m.set(r.id, r.run_name);
          });
          setRunMap(m);
        }
      })
      .catch(() => {});

    getResearchQueriesList({ page: 1, pSize: 10000, isActive: "all" })
      .then((result) => {
        const items = result?.items || [];
        if (Array.isArray(items)) {
          const m = new Map<number, string>();
          items.forEach((q: any) => {
            if (q.id != null) m.set(q.id, q.name);
          });
          setGrqMap(m);
        }
      })
      .catch(() => {});
  }, []);

  const [fetchedData, refetch] =
    useFetchPaginatedResearchQueryRunScoringConfigs({
      setIsLoading,
      page,
      pSize,
      runInstanceId,
      genericResearchQueryId,
      isActive: isActiveFilter,
    });

  // Sync filter state → URL
  useEffect(() => {
    const p = new URLSearchParams();
    // Preserve ScoringConfig's reserved tab params so filter writes don't
    // wipe parent tab state from the URL.
    ["tab", "runSub", "aggSub"].forEach((k) => {
      const v = searchParams.get(k);
      if (v !== null) p.set(k, v);
    });
    FILTER_CONFIG.URL_FIELDS.forEach((f) => {
      const v = searchFilterData[f];
      if (v) p.set(f, String(v));
    });
    if (searchParams.toString() !== p.toString())
      setSearchParams(p, { replace: true });
  }, [searchFilterData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL → filter state (browser back/forward)
  useEffect(() => {
    const s: any = { ...searchFilterData };
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const v = searchParams.get(f);
      if (v) s[f] = parseUrlFilter(f, v);
    });
    const changed = Object.keys(s).some((k) => s[k] !== searchFilterData[k]);
    if (changed) setSearchFilterData(s);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [searchFilterData]);

  useEffect(() => {
    if (fetchedData?.items) setFileData(fetchedData);
  }, [fetchedData]);

  // Apply client-side severity filter
  const visibleItems = severityFilter
    ? (fileData?.items ?? []).filter(
        (row: any) => row.severity === severityFilter
      )
    : fileData?.items ?? [];

  const handleDelete = (row: any) => {
    setItemToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      setDeleteLoading(true);
      await deleteResearchQueryRunScoringConfig(itemToDelete.id);
      showToast(`Config #${itemToDelete.id} deleted`, "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to delete config"),
        "error"
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleReactivate = async (row: any) => {
    setIsLoading(true);
    try {
      await reactivateResearchQueryRunScoringConfig(row.id);
      showToast(`Config #${row.id} reactivated`, "success");
      refetch();
    } catch {
      showToast("Error reactivating config", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
      renderCell: () => <></>,
    },
    { field: "id", headerName: "ID", minWidth: 70, flex: 0.5 },
    {
      field: "run_instance_id",
      headerName: "Run",
      minWidth: 180,
      flex: 1.2,
      renderCell: (p: any) => {
        const id = p.value;
        const name = runMap.get(id);
        return (
          <LimitChractersWithTooltip value={formatEntityLabel(name, id)} />
        );
      },
    },
    {
      field: "generic_research_query_id",
      headerName: "Research Query",
      minWidth: 200,
      flex: 1.5,
      renderCell: (p: any) => {
        const id = p.value;
        const name = grqMap.get(id);
        return (
          <LimitChractersWithTooltip value={formatEntityLabel(name, id)} />
        );
      },
    },
    {
      field: "severity",
      headerName: "Severity",
      minWidth: 110,
      flex: 0.8,
      renderCell: (p: any) => (
        <LimitChractersWithTooltip value={p.value ?? ""} />
      ),
    },
    { field: "factor", headerName: "Factor", minWidth: 90, flex: 0.6 },
    {
      field: "modified_by_user",
      headerName: "Modified By",
      minWidth: 130,
      flex: 1,
      renderCell: (p: any) => (
        <LimitChractersWithTooltip value={p?.value?.username ?? ""} />
      ),
    },
    {
      field: "updated_on",
      headerName: "Last Modified",
      minWidth: 160,
      flex: 1,
      renderCell: (p: any) => getFormattedDateTime(p.value),
    },
    {
      field: "is_active",
      headerName: "Status",
      minWidth: 80,
      flex: 0.6,
      renderCell: (p: any) =>
        p.value ? (
          <Box title="Active">
            <IconCheckCircleIconGreenSvg width={21} height={21} />
          </Box>
        ) : (
          <Box title="Inactive">
            <IconCrossCircleIconSvg width={21} height={21} />
          </Box>
        ),
    },
    {
      field: "action",
      headerName: "Actions",
      align: "right",
      headerAlign: "right",
      width: 140,
      renderCell: (p: any) => {
        const isActive = p?.row?.is_active === true;
        return (
          <>
            {isActive && (
              <>
                <Tooltip title="View Config" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      navigate(`/scoring/research-query-run/view/${p.row.id}`, {
                        state: { rowData: p.row },
                      })
                    }
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip title="Edit Config" placement="top" arrow>
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(
                          `/scoring/research-query-run/edit/${p.row.id}`,
                          { state: { rowData: p.row } }
                        )
                      }
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                <DropdownMenu
                  dropdownMenuItems={[
                    ...(canDelete
                      ? [
                          <IconButton
                            key="delete"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => handleDelete(p.row)}
                          >
                            <IconDeleteBlueSvg /> Delete Config
                          </IconButton>,
                        ]
                      : []),
                  ]}
                />
              </>
            )}
            {!isActive && canEdit && (
              <Tooltip title="Reactivate Config" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(p.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      {(isLoading || deleteLoading) && (
        <Loader isLoading={isLoading || deleteLoading} />
      )}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Research Query Run Scoring Config"
        dailogDescription={`Are you sure you want to delete config #${itemToDelete?.id}?`}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2} alignItems="center">
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={[
                "run_list",
                "generic_research_query_id",
                "status_filter",
                "severity",
              ]}
              primaryFields={["run_list", "generic_research_query_id"]}
              fieldRequirements={{
                defaultStatusFilter: "Active",
                includeRunId: true,
              }}
            />
          </Grid>
          {canAdd && (
            <Grid item display={"flex"} justifyContent={"flex-end"}>
              <Stack>
                <Box sx={{ display: "flex", columnGap: 2 }}>
                  <ButtonComponent
                    handelClickEvent={() =>
                      navigate("/scoring/research-query-run/add")
                    }
                    className="btn-orange"
                  >
                    <AddSharpIcon sx={{ marginRight: "4px" }} /> Research Query
                    Run Scoring
                  </ButtonComponent>
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={visibleItems}
          loading={isLoading}
          dataListTitle="Research Query Run Scoring Config List"
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={360}
        />
      </FlexBetween>
    </Box>
  );
};

export default ResearchQueryRunScoringList;
